import { useEffect, useState } from 'react';
import { Mail, Plus, Sparkles, UsersRound, X } from 'lucide-react';
import { useWorkspace, authHeaders } from '../../context/WorkspaceContext';
import { Alert, Button, EmptyState, FormField, PageHeader, SectionHeader, Skeleton, StatusBadge } from '../../components/ui';
import { useToast } from '../../components/ui/ToastProvider';
import DataTable from '../../components/ui/DataTable';
import './dashboard.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const STAGES = [
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' },
];
const BLANK = { business: '', industry: '', location: '', website: '', contactEmail: '', seoScore: 50, reviewRating: '', socialStatus: 'missing' };

function LeadDrawer({ lead, onClose, onStatus }) {
  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!lead) return null;

  const followUpLink = `/dashboard/ai-studio/followup_writer?${new URLSearchParams({
    business: lead.business || '',
    industry: lead.industry || '',
    location: lead.location || '',
    notes: `Lead saved in the Data Tallied pipeline. Current stage: ${lead.status}.${lead.contact_email ? ` Contact: ${lead.contact_email}.` : ''}`,
  })}`;

  return (
    <>
      <div className="lead-drawer-backdrop" onClick={onClose} />
      <aside className="lead-drawer" role="dialog" aria-modal="true" aria-label={`Lead details for ${lead.business}`}>
        <header>
          <div>
            <h2 className="dt-section-title">{lead.business}</h2>
            <p className="dt-support">{[lead.industry, lead.location].filter(Boolean).join(' · ') || 'No industry recorded'}</p>
          </div>
          <button type="button" className="dt-btn is-secondary is-sm is-icon" onClick={onClose} aria-label="Close lead details"><X size={16} /></button>
        </header>
        <div className="lead-drawer-body">
          <FormField label="Stage" id="lead-stage">
            <select id="lead-stage" className="dt-select" value={lead.status} onChange={event => onStatus(lead.id, event.target.value)}>
              {STAGES.map(stage => <option value={stage.id} key={stage.id}>{stage.label}</option>)}
            </select>
          </FormField>

          <div>
            <h3 className="dt-label">Contact</h3>
            <dl className="lead-detail-list">
              <div><dt>Email</dt><dd>{lead.contact_email || 'Not recorded'}</dd></div>
              <div><dt>Website</dt><dd>{lead.website || 'Not recorded'}</dd></div>
              <div><dt>Location</dt><dd>{lead.location || 'Not recorded'}</dd></div>
              <div><dt>Last update</dt><dd>{new Date(lead.updated_at).toLocaleString()}</dd></div>
            </dl>
          </div>

          <div>
            <h3 className="dt-label">Opportunity signals</h3>
            <dl className="lead-detail-list">
              <div><dt>Website health</dt><dd>{lead.seo_score}/100</dd></div>
              <div><dt>Review rating</dt><dd>{lead.review_rating ? `${lead.review_rating} stars` : 'Not recorded'}</dd></div>
              <div><dt>Social presence</dt><dd style={{ textTransform: 'capitalize' }}>{lead.social_status}</dd></div>
            </dl>
          </div>

          <div className="dt-stack-2">
            <h3 className="dt-label">Next step</h3>
            <Button to={followUpLink}><Sparkles size={15} />Generate follow-up</Button>
            {lead.contact_email && <Button variant="secondary" href={`mailto:${lead.contact_email}`}><Mail size={15} />Email this lead</Button>}
          </div>
        </div>
      </aside>
    </>
  );
}

export default function LeadsPage() {
  const { workspace, loading, reload } = useWorkspace();
  const { notify } = useToast();
  const [view, setView] = useState('pipeline');
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const leads = workspace.prospects || [];
  const selected = leads.find(lead => lead.id === selectedId) || null;

  const change = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));

  const submit = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const response = await fetch(`${API}/workspace/prospects`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not save this lead.');
      setForm(BLANK);
      await reload();
      notify('Lead added to your pipeline.');
    } catch (requestError) {
      setError(requestError.message);
      notify(requestError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (leadId, status) => {
    const response = await fetch(`${API}/workspace/prospects/${leadId}/status`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }) });
    if (response.ok) { await reload(); notify('Lead stage updated.'); }
    else notify('Could not update this lead.', 'error');
  };

  if (loading) return <div className="dt-stack-4"><Skeleton height={80} /><Skeleton height={220} /><Skeleton height={260} /></div>;

  return (
    <div className="dt-stack-5">
      <PageHeader
        title="Leads"
        description="Track and follow up with potential customers."
        actions={<Button href="#add-lead"><Plus size={15} />Add lead</Button>}
      />

      <div className="dt-tabs" role="tablist" aria-label="Lead views">
        <button type="button" role="tab" aria-selected={view === 'pipeline'} onClick={() => setView('pipeline')}>Pipeline</button>
        <button type="button" role="tab" aria-selected={view === 'all'} onClick={() => setView('all')}>All leads ({leads.length})</button>
      </div>

      {!leads.length ? (
        <div className="dt-card">
          <EmptyState
            icon={UsersRound}
            title="No leads yet"
            description="Once someone contacts your business, add them here so every inquiry has an owner and a next step."
            actions={<Button href="#add-lead">Add your first lead</Button>}
          />
        </div>
      ) : view === 'pipeline' ? (
        <section className="pipeline-board" aria-label="Lead pipeline">
          {STAGES.map(stage => {
            const stageLeads = leads.filter(lead => lead.status === stage.id);
            return (
              <div className="pipeline-column" key={stage.id}>
                <div className="pipeline-head"><span>{stage.label}</span><b>{stageLeads.length}</b></div>
                {stageLeads.map(lead => (
                  <button type="button" className="lead-card" key={lead.id} onClick={() => setSelectedId(lead.id)}>
                    <strong>{lead.business}</strong>
                    <small>{lead.industry || 'No industry'}</small>
                    <small>{new Date(lead.updated_at).toLocaleDateString()}</small>
                  </button>
                ))}
                {!stageLeads.length && <p className="pipeline-empty">Empty</p>}
              </div>
            );
          })}
        </section>
      ) : (
        <DataTable
          rows={leads}
          searchKeys={['business', 'industry', 'location', 'contact_email']}
          searchPlaceholder="Search leads"
          initialSort={{ id: 'updated', direction: 'desc' }}
          caption="All leads"
          columns={[
            { id: 'business', header: 'Lead', primary: true, render: row => row.business, sortValue: row => row.business.toLowerCase() },
            { id: 'industry', header: 'Interested in', render: row => row.industry || '—' },
            { id: 'location', header: 'Location', render: row => row.location || '—' },
            { id: 'status', header: 'Stage', render: row => <StatusBadge status={row.status} />, sortValue: row => row.status },
            { id: 'updated', header: 'Updated', render: row => new Date(row.updated_at).toLocaleDateString(), sortValue: row => new Date(row.updated_at).getTime() },
            { id: 'actions', header: '', align: 'right', render: row => <Button variant="secondary" size="sm" onClick={() => setSelectedId(row.id)}>View</Button> },
          ]}
          renderCard={row => (
            <>
              <strong className="dt-card-title">{row.business}</strong>
              <dl>
                <dt>Stage</dt><dd><StatusBadge status={row.status} /></dd>
                <dt>Interested in</dt><dd>{row.industry || '—'}</dd>
                <dt>Updated</dt><dd>{new Date(row.updated_at).toLocaleDateString()}</dd>
              </dl>
              <Button variant="secondary" size="sm" onClick={() => setSelectedId(row.id)}>View lead</Button>
            </>
          )}
        />
      )}

      <section className="dt-card dt-stack-4" id="add-lead" aria-label="Add lead">
        <SectionHeader title="Add a lead" description="Record an inquiry so nothing is lost between the first call and the sale." />
        <form className="dt-stack-4" onSubmit={submit}>
          <div className="dt-field-grid">
            <FormField label="Lead or business name" id="lead-business" required><input id="lead-business" className="dt-input" name="business" value={form.business} onChange={change} placeholder="John Smith" required /></FormField>
            <FormField label="Interested in" id="lead-industry"><input id="lead-industry" className="dt-input" name="industry" value={form.industry} onChange={change} placeholder="Roof repair" /></FormField>
            <FormField label="Location" id="lead-location"><input id="lead-location" className="dt-input" name="location" value={form.location} onChange={change} placeholder="Orlando, FL" /></FormField>
            <FormField label="Website" id="lead-website"><input id="lead-website" className="dt-input" name="website" value={form.website} onChange={change} placeholder="example.com" /></FormField>
            <FormField label="Contact email" id="lead-email"><input id="lead-email" className="dt-input" type="email" name="contactEmail" value={form.contactEmail} onChange={change} placeholder="john@example.com" /></FormField>
            <FormField label="Website health" id="lead-seo" help="Your estimate from 0 to 100."><input id="lead-seo" className="dt-input" type="number" min="0" max="100" name="seoScore" value={form.seoScore} onChange={change} /></FormField>
            <FormField label="Review rating" id="lead-rating"><input id="lead-rating" className="dt-input" type="number" min="0" max="5" step="0.1" name="reviewRating" value={form.reviewRating} onChange={change} placeholder="4.5" /></FormField>
            <FormField label="Social presence" id="lead-social">
              <select id="lead-social" className="dt-select" name="socialStatus" value={form.socialStatus} onChange={change}>
                <option value="missing">Missing</option>
                <option value="partial">Partial</option>
                <option value="active">Active</option>
              </select>
            </FormField>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          <div><Button type="submit" loading={busy}><Plus size={15} />Add lead</Button></div>
        </form>
      </section>

      <LeadDrawer lead={selected} onClose={() => setSelectedId(null)} onStatus={updateStatus} />
    </div>
  );
}
